import type { Game } from 'chessboard/model/game/types';
import type { MoveListComponent } from '../config/types';
import { isCommentRowContent, isMovesRowContent } from '../config/types';

export type RenderKeys = { [key: string]: number | undefined };

/**
 * Gets the unique key for a row's content.
 * Modifies the existing render keys object.
 */
export function getRowRenderKey(
  { content, depth }: Omit<MoveListComponent.Row, 'renderKey'>,
  existingRenderKeys: RenderKeys = {},
): string {
  let renderKey: string;

  if (isMovesRowContent(content)) {
    renderKey = `moves-${depth}-${getNodeRenderKey(content[0].node)}`;
  } else if (isCommentRowContent(content)) {
    const { node, isBeforeComment } = content;
    const commentType = isBeforeComment ? 'before' : 'after';
    const nodeKey = node ? getNodeRenderKey(node) : `(line-${content.lineNumber ?? 'UNKNOWN'})`;

    renderKey = `${commentType}Comment-${depth}-${nodeKey}`;
  } else {
    renderKey = `unknownContent-${JSON.stringify(content)}`;
  }

  return getDedupedRenderKey(renderKey, existingRenderKeys);
}

/**
 * gets the render key for a node
 */
function getNodeRenderKey({ hash, san, moveNumber }: Game.Move.Node): string {
  return `(node-${moveNumber}-${san}-[${hash.toString()}])`;
}

/**
 * Gets the deduped render key based on the existing render keys.
 * Modifies the existingRenderKeys object.
 */
function getDedupedRenderKey(key: string, existingRenderKeys: RenderKeys): string {
  existingRenderKeys[key] = (existingRenderKeys[key] ?? 0) + 1;
  if ((existingRenderKeys[key] ?? 0) > 1) {
    return `${existingRenderKeys[key]}-${key}`;
  }

  return key;
}
